import { QUERY_KEYS } from '../../constants/queryKeys';
import type { ImageSearchResponseType } from '../../types/type';
import CenterScreenWrapper from '../CenterScreenWrapper';
import { t } from 'i18next';
import { useQuery } from 'react-query';
import type { PhotoCardType } from '../../../types/PhotoCardType';
import { Button, SquareBadge, ThumbnailPhotoCard } from '@peacecover/component';
import { ChevronDownSmall, XmarkCircle } from '@peacecover/icon';
import { useEffect, useRef, useState } from 'react';
import HorizontalScroll from '../../../my-page/components/HorizontalScroll';
import { getPhotoCardBadges } from '../../../utils/getPhotoCardBadges';
import LoadingActionsBar from '../../../trading/components/LoadingActionsBar';
import { useDotAnimation } from '../../../hooks/useDotAnimation';

export default function ImageSearchResultList({
  isLoading,
  onBackClick,
  onOpenCandidateList,
  onDeleteClick,
  onCardClick,
  listScrollStartPosition,
  onScrollChange,
  onSellClick,
  onAddToMyPhotoCardClick
}: {
  isLoading: boolean;
  onBackClick: () => void;
  onOpenCandidateList: (resultItem: ImageSearchResponseType['results'][number]) => void;
  onDeleteClick: (position: number) => void;
  onCardClick: (photoCardId: string) => void;
  listScrollStartPosition: number;
  onScrollChange: (scrollPosition: number) => void;
  onSellClick: (searchResult: ImageSearchResponseType) => void;
  onAddToMyPhotoCardClick: (searchResult: ImageSearchResponseType) => void;
}) {
  const listRef = useRef<HTMLDivElement>(null);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const { data: searchResult } = useQuery<ImageSearchResponseType>(
    [QUERY_KEYS.IMAGE_SEARCH_RESULT],
    {
      staleTime: Number.POSITIVE_INFINITY
    }
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: 마운트 시 스크롤 위치 초기화
  useEffect(() => {
    if (listRef.current && listScrollStartPosition) {
      listRef.current.scrollTop = listScrollStartPosition;
    }
  }, []);

  // biome-ignore lint/correctness/useExhaustiveDependencies: 요소 개수 변경시 스크롤 계산
  useEffect(() => {
    if (listRef.current) {
      const hasNoScroll = listRef.current.scrollHeight <= listRef.current.clientHeight;
      setIsAtBottom(hasNoScroll);
    }
  }, [searchResult?.results.length]);

  const dots = useDotAnimation({ isLoading });
  const statusCards = getLoadingStatusCards(dots);

  if (!searchResult) return null;

  //첫 결과 카드가 1장일때 높이 조절을 위한 변수들
  const { titleTopMargin, listTopMargin } = getLayoutMargins(searchResult.numCards);

  return (
    <CenterScreenWrapper
      onBackClick={onBackClick}
      topNavigationTitle={t('imageSearch.searchResult.topNavigationTitle') as string}
    >
      <div className="flex flex-col h-full min-h-0">
        <div
          className="flex-1 overflow-y-auto"
          ref={listRef}
          onScroll={(e) => {
            const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
            const isAtBottom = scrollTop + clientHeight >= scrollHeight - 10;

            const hasNoScroll = scrollHeight <= clientHeight;
            onScrollChange(scrollTop);
            setIsAtBottom(isAtBottom || hasNoScroll);
          }}
        >
          <div
            className="flex flex-col items-center justify-center py-[10px] px-[24px] gap-[14px]"
            style={{
              marginTop: `${titleTopMargin}px`
            }}
          >
            <p className="text-[26px] font-bold text-center leading-tight text-pure-black dark:text-main-dark-1">
              {/* 카드 수만큼 변경 */}
              {t('imageSearch.success.findPhotoCards', {
                count: searchResult.results.length
              })}
            </p>
            <p className="text-main-light-4 text-[15px] font-medium dark:text-main-dark-4 text-center whitespace-pre-line">
              {t('imageSearch.success.findPhotoCardsDescription')}
            </p>
          </div>
          <div
            className="flex flex-col items-center justify-start gap-[12px] px-[24px] mb-[28px]"
            style={{
              marginTop: `${listTopMargin}px`
            }}
          >
            {searchResult.results.map(
              (result) =>
                result.candidates[0].photoCard && (
                  <ImageSuccessResultItem
                    onOpenCandidateList={() => onOpenCandidateList(result)}
                    key={result.originalUrl}
                    photoCard={result.candidates[0].photoCard}
                    onDeleteClick={() => onDeleteClick(result.position)}
                    enableDeleteButton={searchResult.results.length > 1}
                    onCardClick={() =>
                      onCardClick(result.candidates[0].photoCard?.photoCardId ?? '')
                    }
                  />
                )
            )}
          </div>
        </div>
        <div className="relative w-full flex items-center justify-center gap-[8px] px-[24px] z-10 bg-pure-white flex-shrink-0 dark:bg-[#0D0E12] mb-[16px]">
          {!isAtBottom && (
            <div className="absolute -top-[60px] left-0 w-full h-[60px] pointer-events-none bg-[linear-gradient(177deg,rgba(255,255,255,0)_2.59%,#fff_82.05%)] dark:bg-[linear-gradient(177deg,rgba(13,14,18,0)_2.59%,#0D0E12_82.05%)]" />
          )}
          <div className="flex gap-[8px] w-full h-[52px]">
            <Button
              btnColor="secondary"
              size="large"
              className="w-full h-[52px] text-[17px] font-semibold"
              onClick={() => onSellClick(searchResult)}
            >
              {t('imageSearch.success.sell')}
            </Button>
            <Button
              btnColor="primary"
              size="large"
              className="w-full h-[52px] text-[17px] font-semibold"
              onClick={() => onAddToMyPhotoCardClick(searchResult)}
            >
              {t('imageSearch.success.addToMyPhotoCard')}
            </Button>
          </div>
        </div>
      </div>
      {isLoading && <LoadingActionsBar step={0} statusCards={statusCards} className="h-[150px]" />}
    </CenterScreenWrapper>
  );
}

const ImageSuccessResultItem = ({
  photoCard,
  onOpenCandidateList,
  onDeleteClick,
  enableDeleteButton,
  onCardClick
}: {
  photoCard: PhotoCardType;
  onOpenCandidateList: () => void;
  onDeleteClick: () => void;
  enableDeleteButton: boolean;
  onCardClick: () => void;
}) => {
  const badges = getPhotoCardBadges(photoCard);

  return (
    <div
      role="none"
      className="w-full h-[123px] bg-pure-white rounded-[12px] p-[16px] relative dark:bg-[rgba(20,20,20,0.2)] shadow-[0_0_12px_0_rgba(0,0,0,0.05),0_0_3px_0_rgba(0,0,0,0.08)] dark:shadow-[0_0_12px_0_rgba(0,0,0,0.90),0_0_2px_0_#000]"
      onClick={onCardClick}
    >
      <div className="flex gap-[10px] items-center">
        <ThumbnailPhotoCard imageUrl={photoCard.pictureUrl} className="w-[57px] h-[91px]" />
        <div className="flex flex-col gap-[4px]">
          <p className="text-[14px] font-semibold text-main-lv2">
            {t('imageSearch.searchResult.categoryTitle', {
              member: photoCard.memberName,
              category: photoCard.categoryName
            })}
            {photoCard.categoryEmoji}
          </p>
          <p className="text-main-light-4 text-[13px] font-normal dark:text-main-dark-4">
            {photoCard.eventName}
          </p>
          {badges.length > 0 && (
            <HorizontalScroll>
              <div className="flex flex-row gap-[4px] max-w-[220px] mb-[4.5px]">
                {badges.map((badge) => (
                  <SquareBadge
                    key={badge}
                    size="small"
                    colorVariant="gray"
                    styleVariant="solid"
                    className="bg-surface-lv2"
                  >
                    {badge}
                  </SquareBadge>
                ))}
              </div>
            </HorizontalScroll>
          )}
        </div>
      </div>
      {/* 오른쪽 상단 X 버튼 */}
      {enableDeleteButton && (
        <div className="absolute top-[8px] right-[8px]">
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDeleteClick();
            }}
          >
            <XmarkCircle className="w-[24px] h-[24px] text-main-light-3" />
          </button>
        </div>
      )}
      {/* 오른쪽 하단 텍스트 버튼 */}
      <div className="absolute bottom-[10px] right-[8px]">
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onOpenCandidateList();
          }}
        >
          <p className="text-[12px] font-medium text-text-primary flex items-center relative">
            {t('imageSearch.success.notFoundPhotoCard')}
            <ChevronDownSmall className="w-[16px] h-[16px] rotate-[-90deg]" />
          </p>
        </button>
      </div>
    </div>
  );
};

const getLayoutMargins = (numCards: number) => {
  const isSinglePhotoCard = numCards === 1;
  return {
    titleTopMargin: isSinglePhotoCard ? 128 : 20,
    listTopMargin: isSinglePhotoCard ? 54 : 12
  };
};

const getLoadingStatusCards = (dots: string) => [
  {
    icon: '3D.loading',
    title: t('NewTradingPhotoCards.loadingMessage', { dots }),
    subtitle: t('NewTradingPhotoCards.loadingActionsBarSubtitleInProgress', { dots }),
    iconClassName: 'w-[32px] h-[32px] mt-[12px]'
  }
];
